'use client';

// Inline-editable "prospection" cells shared by the 3 shortlist renderers
// (LeadRenderer / PortfolioFitRenderer / UnitFitRenderer). The user writes these
// manual placeholders directly on each row: contacted? / channel / response /
// comment. Contacted, channel and response render as Notion-style coloured pills
// and open a Notion-style dropdown of pill options; each has a DEFAULT state shown
// when the field is unset. Each cell takes the value + an onChange that the
// renderer wires to `kernel.updateEntity(<fit>, id, patch)`. Click handlers
// stopPropagation so editing never triggers the row's open/navigation.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Check } from '@/components/ui/lucide-memo';
import { NOTION_DARK_COLORS, type NotionColorName } from '@/components/ui/notionColors';

type Lang = 'en' | 'fr';

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/** Frozen per-colour style objects, so a pill never instantiates one at render time. */
const PILL_STYLES = Object.fromEntries(
  (Object.keys(NOTION_DARK_COLORS) as NotionColorName[]).map((name) => [
    name,
    {
      pill: { backgroundColor: NOTION_DARK_COLORS[name].bg, color: NOTION_DARK_COLORS[name].text },
      dot: { backgroundColor: NOTION_DARK_COLORS[name].text },
    },
  ]),
) as Record<NotionColorName, { pill: React.CSSProperties; dot: React.CSSProperties }>;

/** A coloured dot and a plain word (owner, 2026-09-28): ChatGPT marks a state, it does not box it. */
const NotionPill = React.memo(function NotionPill({ color, label }: { color: NotionColorName; label: string }) {
  const s = PILL_STYLES[color];
  return (
    <span className="inline-flex max-w-full items-center gap-2 leading-none text-[#CDCDCD]" style={LABEL_STYLE}>
      <span className="h-[7px] w-[7px] shrink-0 rounded-full" style={s.dot} />
      <span className="truncate" style={LABEL_STYLE}>{label}</span>
    </span>
  );
});
const LABEL_STYLE = { fontSize: 13 } as const;

interface SelectOption<V> {
  key: string;
  label: string;
  color: NotionColorName;
  value: V;
}

/**
 * One dropdown row. Memoized and keyed by the option key (not its value), so the
 * select handler stays a single stable callback instead of one closure per row.
 */
const SelectOptionRow = React.memo(function SelectOptionRow({
  optionKey,
  label,
  color,
  checked,
  onSelect,
}: {
  optionKey: string;
  label: string;
  color: NotionColorName;
  checked: boolean;
  onSelect: (optionKey: string) => void;
}) {
  const handleClick = useCallback(() => onSelect(optionKey), [onSelect, optionKey]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className="flex h-9 w-full items-center justify-between gap-2 rounded-[10px] px-2.5 text-left transition-colors hover:bg-white/[0.08]"
    >
      <NotionPill color={color} label={label} />
      {checked ? <Check size={13} className="shrink-0 text-[#9B9B9B]" /> : null}
    </button>
  );
});

/**
 * Generic Notion-style single-select: a coloured pill in the cell that opens a
 * portal dropdown of coloured pill options with a filter input. The FIRST option
 * is the default state — rendered whenever the stored value matches no option
 * (i.e. unset). The portal escapes the table cell's `overflow:hidden`.
 */
function NotionSelectCell<V extends string | boolean | null>({
  value,
  options,
  onChange,
  lang,
}: {
  value: V;
  options: SelectOption<V>[];
  onChange: (next: V) => void;
  lang: Lang;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [query, setQuery] = useState('');
  const btnRef = useRef<HTMLButtonElement>(null);

  const current = options.find((o) => o.value === value) ?? options[0]!;

  const toggle = useCallback(() => {
    if (open) { setOpen(false); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.left });
    setQuery('');
    setOpen(true);
  }, [open]);

  const handleQuery = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value), []);

  const handleSelect = useCallback((optionKey: string) => {
    const picked = options.find((o) => o.key === optionKey);
    if (picked) onChange(picked.value);
    setOpen(false);
  }, [options, onChange]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onDoc = (e: MouseEvent) => {
      if (btnRef.current && !btnRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const portalStyle = useMemo(() => ({ top: pos?.top, left: pos?.left }), [pos]);

  return (
    <div className="flex h-full min-w-0 items-center" onClick={stop} onMouseDown={stop}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="flex h-8 min-w-0 max-w-full items-center rounded-full px-2.5 transition-colors hover:bg-white/[0.06]"
      >
        <NotionPill color={current.color} label={current.label} />
      </button>
      {open && pos
        ? createPortal(
            <div
              onMouseDown={stop}
              onClick={stop}
              className="fixed z-[1000] w-[220px] overflow-hidden rounded-[16px] border border-white/[0.06] bg-[#353535] shadow-[0_18px_52px_rgba(0,0,0,0.5)]"
              style={portalStyle}
            >
              <div className="p-1.5">
                <input
                  autoFocus
                  value={query}
                  onChange={handleQuery}
                  placeholder={lang === 'fr' ? 'Rechercher…' : 'Search…'}
                  className="h-8 w-full rounded-full border border-white/[0.08] bg-transparent px-3 text-[13px] text-white outline-none placeholder:text-[#AFAFAF]"
                />
              </div>
              <div className="custom-scrollbar max-h-[240px] overflow-y-auto px-1.5 pb-1.5">
                {filtered.map((o) => (
                  <SelectOptionRow
                    key={o.key}
                    optionKey={o.key}
                    label={o.label}
                    color={o.color}
                    checked={o.value === current.value}
                    onSelect={handleSelect}
                  />
                ))}
                {filtered.length === 0 ? (
                  <div className="px-2 py-2 text-[12px] text-[#6E6E6E]">{lang === 'fr' ? 'Aucun résultat' : 'No results'}</div>
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

// --- Option sets (first entry = default state) ---
// Frozen per-language lists: they depend on nothing but `lang`, so they live at
// module scope and every cell passes the very same array reference.

const CONTACTED_OPTIONS: Record<Lang, SelectOption<boolean>[]> = {
  en: [
    { key: 'no', label: 'No', color: 'gray', value: false },
    { key: 'yes', label: 'Yes', color: 'green', value: true },
  ],
  fr: [
    { key: 'no', label: 'Non', color: 'gray', value: false },
    { key: 'yes', label: 'Oui', color: 'green', value: true },
  ],
};

const CHANNEL_OPTIONS: Record<Lang, SelectOption<string | null>[]> = {
  en: [
    { key: 'none', label: 'Not set', color: 'gray', value: null },
    { key: 'PHONE', label: 'Phone', color: 'blue', value: 'PHONE' },
    { key: 'EMAIL', label: 'Email', color: 'green', value: 'EMAIL' },
    { key: 'LINKEDIN', label: 'LinkedIn', color: 'purple', value: 'LINKEDIN' },
    { key: 'IN_PERSON', label: 'In person', color: 'orange', value: 'IN_PERSON' },
    { key: 'OTHER', label: 'Other', color: 'gray', value: 'OTHER' },
  ],
  fr: [
    { key: 'none', label: 'Non défini', color: 'gray', value: null },
    { key: 'PHONE', label: 'Appel', color: 'blue', value: 'PHONE' },
    { key: 'EMAIL', label: 'Email', color: 'green', value: 'EMAIL' },
    { key: 'LINKEDIN', label: 'LinkedIn', color: 'purple', value: 'LINKEDIN' },
    { key: 'IN_PERSON', label: 'En personne', color: 'orange', value: 'IN_PERSON' },
    { key: 'OTHER', label: 'Autre', color: 'gray', value: 'OTHER' },
  ],
};

const RESPONSE_OPTIONS: Record<Lang, SelectOption<string | null>[]> = {
  en: [
    { key: 'none', label: 'Awaiting', color: 'gray', value: null },
    { key: 'POSITIVE', label: 'Positive', color: 'green', value: 'POSITIVE' },
    { key: 'NEGATIVE', label: 'Negative', color: 'red', value: 'NEGATIVE' },
  ],
  fr: [
    { key: 'none', label: 'En attente', color: 'gray', value: null },
    { key: 'POSITIVE', label: 'Positif', color: 'green', value: 'POSITIVE' },
    { key: 'NEGATIVE', label: 'Négatif', color: 'red', value: 'NEGATIVE' },
  ],
};

/** 1. "Have we called the tenant?" — Non (default) / Oui pill select. */
export const ProspectionContactedCell = React.memo(function ProspectionContactedCell({
  value,
  lang,
  onChange,
}: {
  value?: boolean | null;
  lang: Lang;
  onChange: (next: boolean) => void;
}) {
  return <NotionSelectCell value={!!value} options={CONTACTED_OPTIONS[lang]} onChange={onChange} lang={lang} />;
});

/** 2. Channel — Non défini (default) / Appel / Email / LinkedIn / En personne / Autre. */
export const ProspectionChannelCell = React.memo(function ProspectionChannelCell({
  value,
  lang,
  onChange,
}: {
  value?: string | null;
  lang: Lang;
  onChange: (next: string | null) => void;
}) {
  return <NotionSelectCell value={value ?? null} options={CHANNEL_OPTIONS[lang]} onChange={onChange} lang={lang} />;
});

/** 3. Response — En attente (default) / Positif / Négatif. */
export const ProspectionResponseCell = React.memo(function ProspectionResponseCell({
  value,
  lang,
  onChange,
}: {
  value?: string | null;
  lang: Lang;
  onChange: (next: string | null) => void;
}) {
  return <NotionSelectCell value={value ?? null} options={RESPONSE_OPTIONS[lang]} onChange={onChange} lang={lang} />;
});

/** 4. Free-text comment — saves on blur / Enter when changed. */
/**
 * The server accepts 2000 characters (mappers.ts, prospectionComment). Enforced in the editor
 * because a write over the limit is REJECTED, and a rejected write on a comment box looks exactly
 * like a saved one: the panel closes and the text is gone.
 */
const COMMENT_MAX = 2000;

/**
 * The comment cell: a one-line field for a quick note, and a real window for a real note.
 *
 * A comment is the one prospection field with nothing to pick from a list, so it is where people
 * write the sentence that matters, and a 220px cell truncates it to nothing. Double-click opens a
 * panel big enough to read and write the whole thing, the same gesture and the same portal idiom
 * the select cells above already use to escape the row's `overflow:hidden`.
 *
 * The inline input stays exactly as it was, so a two-word note still costs one click.
 */
export const ProspectionCommentCell = React.memo(function ProspectionCommentCell({
  value,
  lang,
  onChange,
}: {
  value?: string | null;
  lang: Lang;
  onChange: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const [expanded, setExpanded] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-existing pattern in this file; changing when this state settles is a behavioural change
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = (text: string) => {
    const next = text.trim().slice(0, COMMENT_MAX) || null;
    if (next !== (value ?? null)) onChange(next);
  };

  const PANEL_W = 340;
  const PANEL_H = 260;
  const openPanel = () => {
    const r = cellRef.current?.getBoundingClientRect();
    if (r) {
      // Clamped to the viewport: this panel is far bigger than the select dropdowns, and a row near
      // the bottom or the right edge would otherwise open it half off screen.
      const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8));
      const below = r.bottom + 6;
      const top = below + PANEL_H > window.innerHeight - 8
        ? Math.max(8, r.top - PANEL_H - 6)
        : below;
      setPos({ top, left });
    }
    setExpanded(true);
  };

  const closePanel = (save: boolean) => {
    if (save) commit(draft);
    else setDraft(value ?? '');
    setExpanded(false);
  };

  useEffect(() => {
    if (!expanded) return;
    areaRef.current?.focus();
    // Caret at the end, so reopening a long note does not land you at character zero.
    const len = areaRef.current?.value.length ?? 0;
    areaRef.current?.setSelectionRange(len, len);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;
    // Scroll/resize CLOSES rather than repositions (same as the select cells), and closing saves:
    // losing a paragraph because the table scrolled would be indefensible.
    const onScrollOrResize = () => closePanel(true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
    // closePanel is deliberately absent: it reads the live draft, so listing it here would re-bind
    // the scroll and resize listeners on every keystroke.
  }, [expanded, draft, value]);

  const remaining = COMMENT_MAX - draft.length;

  return (
    <div
      ref={cellRef}
      className="flex h-full w-full min-w-0 items-center"
      onClick={stop}
      onMouseDown={stop}
      onDoubleClick={(e) => { stop(e); openPanel(); }}
    >
      <input
        value={draft.split('\n')[0] ?? ''}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (!expanded) commit(draft); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setDraft(value ?? ''); e.currentTarget.blur(); }
        }}
        placeholder={lang === 'fr' ? 'Ajouter…' : 'Add…'}
        title={draft || undefined}
        className="w-full rounded bg-transparent px-1 py-0.5 text-[11px] text-[#D4D4D4] outline-none transition-colors placeholder:text-[#565656] hover:bg-[#1E1E1E] focus:bg-[#1E1E1E]"
      />
      {expanded && pos
        ? createPortal(
            <>
              {/* Catches the click that closes the panel. Transparent, not dimmed: the row behind
                  is the context you are commenting on. */}
              <div className="fixed inset-0 z-[1000]" onMouseDown={() => closePanel(true)} />
              <div
                onMouseDown={stop}
                onClick={stop}
                className="fixed z-[1001] flex flex-col overflow-hidden rounded-[16px] border border-white/[0.06] bg-[#353535] shadow-[0_16px_40px_rgba(0,0,0,0.55)]"
                style={{ top: pos.top, left: pos.left, width: PANEL_W, height: PANEL_H }}
              >
                <textarea
                  ref={areaRef}
                  value={draft}
                  maxLength={COMMENT_MAX}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter writes a newline, which is the point of the big box. Escape reverts,
                    // Cmd/Ctrl+Enter is the deliberate save-and-close.
                    if (e.key === 'Escape') { e.preventDefault(); closePanel(false); }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); closePanel(true); }
                  }}
                  placeholder={lang === 'fr' ? 'Écrire un commentaire…' : 'Write a comment…'}
                  className="custom-scrollbar min-h-0 flex-1 resize-none bg-transparent p-3 text-[12.5px] leading-relaxed text-[#D4D4D4] outline-none placeholder:text-[#565656]"
                />
                <div className="flex items-center justify-between border-t border-[#2A2A2A] px-3 py-1.5 text-[10.5px] text-[#6E6E6E]">
                  <span>
                    {lang === 'fr' ? 'Échap pour annuler · ⌘↩ pour enregistrer' : 'Esc to cancel · ⌘↩ to save'}
                  </span>
                  {remaining <= 200 ? (
                    <span className={clsx(remaining <= 0 ? 'text-[#E0603A]' : 'text-[#8A8A8A]')}>
                      {remaining}
                    </span>
                  ) : null}
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </div>
  );
});

/** Localized column headers, so each table can label the 4 columns consistently. */
export const prospectionColumnHeaders = (lang: Lang) => ({
  contacted: lang === 'fr' ? 'Contacté' : 'Contacted',
  channel: lang === 'fr' ? 'Canal' : 'Channel',
  response: lang === 'fr' ? 'Réponse' : 'Response',
  comment: lang === 'fr' ? 'Commentaire' : 'Comment',
});
